<template>
  <div>
    <v-autocomplete
      v-if="autocomplete"
      v-model="selectedModelId"
      :name="name"
      :label="label"
      :clear-on-select="clearOnSelect"
      :multiple="multiple"
      :disabled="isDisabled"
      :error="error"
      :error-messages="errorMessages"
      :items="filteredModels"
      :item-title="labelFieldName"
      :item-value="idFieldName"
      :loading="isLoading"
      clearable
      :hint="hint"
      :persistent-hint="persistentHint"
      :return-object="returnObject"
      :hide-details="hideDetails"
      @update:model-value="setSelectedModel"
    >
      <template #item="{ props }">
        <v-list-item v-bind="props" />
      </template>
    </v-autocomplete>

    <v-select
      v-else
      v-model="selectedModelId"
      :items="filteredModels"
      :item-title="labelFieldName"
      :item-value="idFieldName"
      :name="name"
      :label="label"
      :multiple="multiple"
      :disabled="isDisabled"
      :error="error"
      :error-messages="errorMessages"
      clearable
      :hint="hint"
      :persistent-hint="persistentHint"
      :return-object="returnObject"
      :hide-details="hideDetails"
      :loading="isLoading"
      @update:model-value="setSelectedModel"
    />

    <v-alert
      v-model="errorShown"
      closable
      icon="error"
      type="error"
      :text="errorMessage"
      @click:close="loadAllData"
    />
  </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import {
  computed,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useDataTableUtilities } from '@/composables';

const properties = withDefaults(defineProps<{
  name?: string;
  multiple?: boolean;
  disabled?: boolean;
  clearOnSelect?: boolean;
  indexRoute: string;
  labelFieldName?: string;
  idFieldName?: string;
  searchFields?: Array<string>;
  includes?: Array<string>;
  filters?: Array<SearchFilter>;
  sortFieldName?: string;
  state?: boolean;
  recordLimit?: number;
  error?: boolean;
  errorMessages?: Array<string>;
  label?: string;
  perPage?: number;
  fields?: Array<string>;
  hint?: string;
  persistentHint?: boolean;
  returnObject?: boolean;
  hideDetails?: boolean;
  indexRouteParameters?: any;
  autocomplete?: boolean;
}>(), {
  multiple: () => false,
  perPage: () => 50,
  disabled: () => false,
  includes: () => [],
  clearOnSelect: () => false,
  labelFieldName: () => 'name',
  idFieldName: () => 'id',
  sortFieldName: () => 'name',
  error: () => false,
  errorMessages: () => [],
  fields: () => [],
  filters: () => [],
  persistentHint: () => true,
  returnObject: () => false,
  hideDetails: () => false,
  autocomplete: () => true,
});

const { t } = useI18n();

const { appendFilters } = useDataTableUtilities();
const isLoading = ref(true);
const selectedModelId = defineModel<any>();
const selectedModel = defineModel<any>('selectedModel', { default: null });
/** The label and id fields are chosen by the caller, so rows are read by string key. */
const loadedModels = defineModel<Array<App.Models.BaseModel & Record<string, any>>>('loadedModels', {
  default: () => [],
});
const searchString = ref('');
const errorShown = ref(false);
const errorMessage = ref<any>(null);

const isDisabled = computed(() => properties.disabled || isLoading.value);

const filteredModels = computed<Array<App.Models.BaseModel & Record<string, any>>>(() => {
  if (loadedModels.value.length === 0) {
    return [];
  }

  return loadedModels.value.filter((el) => {
    return properties.labelFieldName in el && el[properties.labelFieldName].includes(searchString.value);
  });
});

function setSelectedModel() {
  if (!loadedModels.value || selectedModelId.value === undefined || selectedModelId.value === null) {
    return;
  }

  selectedModel.value = loadedModels.value.find(
    (model) => model && properties.idFieldName in model && model[properties.idFieldName] === selectedModelId.value,
  ) || null;
}

async function loadAllData() {
  loadedModels.value = [];

  let currentPage = 0;
  let lastPage = 1;
  while (currentPage < lastPage) {
    currentPage++;

    const queryData = {
      page: currentPage,
      per_page: properties.perPage,
      fields: properties.fields.join(','),
      inc: properties.includes,
      sort: properties.sortFieldName,
      ...properties.indexRouteParameters,
    };

    try {
      const response = await axios.get(appendFilters(properties.indexRoute, properties.filters), { params: queryData });
      loadedModels.value = loadedModels.value.concat(response.data.data);
      lastPage = response.data.last_page;
    }
    catch (error) {
      errorMessage.value = t('errors.cannot_load_try_again');
      errorShown.value = true;
      console.error(error);
      break;
    }
  }
  checkIdAndFieldNameInDataSet();
  isLoading.value = false;
  setSelectedModel();
}

function checkIdAndFieldNameInDataSet() {
  if (loadedModels.value.length > 0) {
    if (loadedModels.value.some((model) => {
      return !(properties.idFieldName in model);
    })) { console.warn('The specified idFieldName does not exist in all the loaded models'); }
    if (loadedModels.value.some((model) => {
      return !(properties.labelFieldName in model);
    })) { console.warn('The specified labelFieldName does not exist in all the loaded models'); }
  }
}

loadAllData();
</script>
